/**
 * Getting help.
 *
 * The form asks for the two things only the person knows — what it is about, and what happened.
 * Which agency, which plan, which account: all attached by the server from the session, so a
 * first reply can be an answer rather than four questions.
 *
 * **Every message becomes a ticket with a reference**, listed underneath. Without it the agency
 * has no way to tell whether their message arrived, nothing to quote, and no record of what they
 * have already asked.
 *
 * **It reports what really happened.** With no mail server configured the mail is recorded and
 * printed rather than sent — the ticket exists either way, and the page says which. "Sent" when
 * nothing was sent is the one thing a support form must never do.
 *
 * The destination address is not shown. It is ours, not theirs, and printing it invites people to
 * write around the form, losing the very details the form exists to attach.
 */

import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFormat } from "@/lib/format";
import type { ListParams, PageMeta } from "@/lib/list-params";
import { Pagination } from "@/components/list-toolbar";
import { IconChevronDown } from "@/components/icons";
import { formValues, useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from "@/components/ui";

interface RaisedResult {
  id: string;
  reference: string;
  ok: boolean;
  /** What happened to the *mail*. The ticket exists whichever it is. */
  status: "sent" | "simulated";
}

interface Ticket {
  id: string;
  reference: string;
  topic: string;
  subject: string;
  status: "open" | "answered" | "closed";
  deliveryStatus: string;
  raisedByName: string;
  createdAt: string;
  lastMessageAt: string;
  /** One to five once it has been rated; null while it has not, which is not the same as zero. */
  rating: number | null;
  review?: string | null;
  closedAt: string | null;
  /** Everything after the opening message. Zero means nobody has added to it yet. */
  replies: number;
}

interface ThreadMessage {
  id: string;
  author: "agency" | "support";
  body: string;
  authorName: string;
  deliveryStatus: string;
  createdAt: string;
}

const TOPICS = [
  { value: "problem", label: "Something is broken" },
  { value: "question", label: "A question about how it works" },
  { value: "billing", label: "Billing or my plan" },
  { value: "data", label: "Data, an import, or an export" },
  { value: "other", label: "Something else" },
];

/**
 * Five stars, filled to `value`.
 *
 * With `onPick` they are buttons; without it they are a read-back of what was chosen. One
 * component for both so a rated ticket and an unrated one cannot drift apart visually.
 */
function Stars({ value, onPick, small }: { value: number; onPick?: (next: number) => void; small?: boolean }) {
  const size = small ? 13 : 20;
  return (
    <span className="flex items-center gap-0.5" role={onPick ? "radiogroup" : undefined} aria-label="Rating">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= value;
        const mark = (
          <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 3.6 14.4 9l5.9.5-4.5 3.9 1.4 5.8L12 16.1 6.8 19.2l1.4-5.8L3.7 9.5 9.6 9z"
              fill={filled ? "#F59E0B" : "none"}
              stroke={filled ? "#F59E0B" : "#C4CFCD"}
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
          </svg>
        );

        return onPick ? (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} out of 5`}
            onClick={() => onPick(star)}
            className="rounded p-0.5 hover:bg-ink-100"
          >
            {mark}
          </button>
        ) : (
          <span key={star}>{mark}</span>
        );
      })}
    </span>
  );
}

/** Open is amber, answered is green, closed is quiet — the same language as every other list. */
const STATUS_TONE: Record<Ticket["status"], "warn" | "success" | "muted"> = {
  open: "warn",
  answered: "success",
  closed: "muted",
};

/**
 * The questions that arrive most often, answered where they are asked.
 *
 * Every one of these is answerable from inside the app, which is the test for belonging here: a
 * question whose answer is "write to us" is not an FAQ, it is a ticket.
 */
const FAQS = [
  {
    question: "A figure looks wrong. Where do I check?",
    answer:
      "Open the invoice or payment behind it. Every number on the dashboard opens the list it came from, and the detail usually explains the difference — a part payment, a late fee, or a charge raised after the invoice.",
  },
  {
    question: "A reminder did not arrive. What happened?",
    answer:
      "Reminders logs everything, including what was held back. The same reminder is only sent once per invoice per day, so a second attempt on the same day is recorded as a duplicate rather than sent.",
  },
  {
    question: "Why can a team member not see a property?",
    answer:
      "Agents see only the properties assigned to them. Team, under Operations, is where that is set — managers and owners see the whole portfolio.",
  },
  {
    question: "I have lost my authenticator. How do I get back in?",
    answer:
      "Use one of the ten recovery codes issued when you switched it on. If those are gone too, raise a ticket here: it cannot be reset from inside the app, by design.",
  },
  {
    question: "Can I charge agency and caution fees only when somebody moves in?",
    answer:
      "Yes, and that is the default. Move-in charges are raised once for the tenancy rather than every cycle, and you can raise a service charge at any time on top. A returning tenant starting a new tenancy pays them again.",
  },
  {
    question: "Can a property have more than one landlord?",
    answer:
      "Yes. Each unit can carry its own landlord, so a block sold flat by flat is one property with several owners — and each landlord's statement covers only their own units.",
  },
  {
    question: "How do I change what things are called?",
    answer:
      "Settings, under Vocabulary. Choose the noun for each thing your agency manages — estates, shops, occupants — and it changes everywhere, including on invoices and reminders.",
  },
] as const;

/** What the filter offers. `all` is not a status, which is why it is not in `SUPPORT_STATUSES`. */
const FILTERS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "answered", label: "Answered" },
  { value: "closed", label: "Closed" },
] as const;

type Filter = (typeof FILTERS)[number]["value"];

export function SupportPage() {
  const { user } = useSession();
  const fmt = useFormat();
  const [raised, setRaised] = useState<RaisedResult | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(1);

  /**
   * Which ticket is being read, held in the URL rather than in state.
   *
   * A reload, a back button, or a link pasted to a colleague all have to land on the same ticket.
   * In component state they landed on a blank form instead, which reads as the app having lost
   * what you were looking at.
   *
   * `replace` on the way in, so opening five tickets does not leave five entries to press Back
   * through — but a real entry when returning to the list, because Back from a ticket should go
   * to the list rather than off the page.
   */
  const [params, setParams] = useSearchParams();
  const openId = params.get("ticket");
  const openTicket = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("ticket", id);
    setParams(next, { replace: Boolean(openId) });
  };
  const closePanel = () => {
    const next = new URLSearchParams(params);
    next.delete("ticket");
    setParams(next);
  };

  /**
   * Filtered by the server, not in the browser.
   *
   * The list pages at ten, so filtering what arrived would show "three closed" out of the page it
   * happened to fetch rather than out of all of them — a count that is quietly wrong.
   */
  const tickets = useQuery({
    queryKey: ["support-tickets", filter, page],
    queryFn: () =>
      api.get<{ tickets: Ticket[]; page: PageMeta }>(
        `/support/tickets?page=${page}${filter === "all" ? "" : `&status=${filter}`}`,
      ),
  });

  /**
   * The pager, driven by local state rather than the URL.
   *
   * `Pagination` only ever calls `setPage`; the rest of `ListParams` is there so one component
   * serves both kinds of list rather than growing a second, near-identical copy. Not in the query
   * string because this is a panel beside a form, not a page somebody links to.
   */
  const pager: ListParams = {
    text: "",
    q: "",
    page,
    setText: () => {},
    setPage,
    clear: () => setPage(1),
    pending: false,
  };

  /** Leave whatever is open and start a fresh ticket. */
  const startNewTicket = () => {
    closePanel();
    setRaised(null);
  };

  const choose = (value: Filter) => {
    setFilter(value);
    // A filter change with page 4 still set shows an empty panel and reads as "nothing here".
    setPage(1);
  };

  const write = useApiMutation({
    mutationFn: (input: Record<string, string>) => api.post<RaisedResult>("/support", input),
    invalidates: [["support-tickets"]],
    onSuccess: (result) => {
      setRaised(result);
      // A new ticket is open and belongs at the top of an unfiltered first page.
      setFilter("all");
      setPage(1);
      closePanel();
    },
  });

  const thread = useQuery({
    queryKey: ["support-thread", openId],
    queryFn: () => api.get<{ ticket: Ticket; messages: ThreadMessage[] }>(`/support/tickets/${openId}`),
    enabled: Boolean(openId),
    // A hand-typed or stale id 404s; retrying it three times only delays saying so.
    retry: false,
  });

  const close = useApiMutation({
    mutationFn: (id: string) => api.post(`/support/tickets/${id}/close`, {}),
    invalidates: [["support-tickets"], ["support-thread"]],
  });

  const rate = useApiMutation({
    mutationFn: (input: { id: string; rating: number; review: string }) =>
      api.post(`/support/tickets/${input.id}/rate`, { rating: input.rating, review: input.review }),
    invalidates: [["support-tickets"], ["support-thread"]],
  });
  /** Held while they pick, so the review can be typed before anything is sent. */
  const [stars, setStars] = useState(0);

  const reply = useApiMutation({
    mutationFn: (input: { id: string; message: string }) =>
      api.post<{ reopened: boolean }>(`/support/tickets/${input.id}/reply`, { message: input.message }),
    invalidates: [["support-tickets"], ["support-thread"]],
  });

  const sendRating = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    if (!openId || !stars) return;
    rate.mutate({ id: openId, rating: stars, review: String(new FormData(form).get("review") ?? "").trim() });
    setStars(0);
  };

  const sendReply = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    const value = String(new FormData(form).get("reply") ?? "").trim();
    if (!openId || !value) return;
    reply.mutate({ id: openId, message: value });
    form.reset();
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    write.mutate({ ...formValues(form), page: window.location.href });
    // Cleared here rather than in `onSuccess`, because by then the form element this handler
    // captured may already have been replaced.
    form.reset();
  };

  return (
    <div className="max-w-6xl">
      <PageHeader
        title="Support"
        description="Send us a message and we will reply by email. Every one becomes a ticket you can follow here."
      />

      {/* Reading and writing happen in the same place on the left; the list of what has been
          written stays on the right, so opening a ticket never costs you sight of the others. */}
      <div className="grid items-start gap-5 lg:grid-cols-12">
        <div className="space-y-5 lg:col-span-7">
          {openId ? (
            <Card>
              {thread.error ? (
                <>
                  <Alert tone="danger">That ticket could not be found. It may belong to another agency.</Alert>
                  <div className="mt-4">
                    <Button size="sm" onClick={startNewTicket}>
                      New ticket
                    </Button>
                  </div>
                </>
              ) : thread.isLoading || !thread.data ? (
                <Spinner />
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="mono text-xs font-medium text-ink-500">{thread.data.ticket.reference}</span>
                    <Badge tone={STATUS_TONE[thread.data.ticket.status]} dot>
                      {thread.data.ticket.status}
                    </Badge>

                    <div className="ml-auto flex items-center gap-2">
                      {thread.data.ticket.status !== "closed" ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={close.isPending}
                          onClick={() => close.mutate(thread.data.ticket.id)}
                        >
                          Close ticket
                        </Button>
                      ) : null}
                      <Button size="sm" onClick={startNewTicket}>
                        New ticket
                      </Button>
                    </div>
                  </div>

                  <h2 className="mt-1 text-base font-semibold text-ink-900">{thread.data.ticket.subject}</h2>

                  <ul className="mt-4 space-y-3">
                    {thread.data.messages.map((entry) => (
                      <li
                        key={entry.id}
                        className={`rounded-[var(--radius-surface)] p-3 ${
                          entry.author === "agency" ? "bg-ink-50" : "bg-brand-50"
                        }`}
                      >
                        <p className="whitespace-pre-wrap text-sm text-ink-800">{entry.body}</p>
                        <p className="mt-1.5 text-xs text-ink-500">
                          {entry.authorName} · {fmt.dateTime(entry.createdAt)}
                          {entry.deliveryStatus === "simulated" ? " · not delivered" : ""}
                          {entry.deliveryStatus === "failed" ? " · delivery failed" : ""}
                        </p>
                      </li>
                    ))}
                  </ul>

                  {/*
                    Asked here, under the conversation it is about, and only once the ticket is
                    closed — a rating collected while somebody is still waiting measures their
                    patience rather than the answer. Inline rather than a dialogue: a modal over a
                    thread somebody may still want to read is a toll gate, not a question.
                  */}
                  {thread.data.ticket.status === "closed" ? (
                    <div className="mt-4 rounded-[var(--radius-surface)] border border-ink-200 bg-ink-50 p-3">
                      {thread.data.ticket.rating ? (
                        <div className="flex items-center gap-2">
                          <Stars value={thread.data.ticket.rating} />
                          <span className="text-xs text-ink-500">Thank you — rated {thread.data.ticket.rating}/5.</span>
                        </div>
                      ) : (
                        <form onSubmit={sendRating} className="space-y-2">
                          <p className="text-sm font-medium text-ink-800">How did we do?</p>
                          <Stars value={stars} onPick={setStars} />
                          {stars ? (
                            <>
                              <Input
                                name="review"
                                maxLength={500}
                                placeholder="Anything you would like to add? (optional)"
                                aria-label="Review"
                              />
                              <Button type="submit" size="sm" disabled={rate.isPending}>
                                {rate.isPending ? "Sending…" : "Send rating"}
                              </Button>
                            </>
                          ) : null}
                          {rate.errorMessage ? <Alert tone="danger">{rate.errorMessage}</Alert> : null}
                        </form>
                      )}
                    </div>
                  ) : null}

                  {reply.errorMessage ? (
                    <div className="mt-3">
                      <Alert tone="danger">{reply.errorMessage}</Alert>
                    </div>
                  ) : null}

                  <form onSubmit={sendReply} className="mt-4 space-y-2">
                    <Textarea name="reply" rows={3} required placeholder="Add to this ticket…" aria-label="Reply" />
                    <div className="flex items-center gap-3">
                      <Button type="submit" size="sm" disabled={reply.isPending}>
                        {reply.isPending ? "Sending…" : "Reply"}
                      </Button>
                      {thread.data.ticket.status === "closed" ? (
                        <span className="text-xs text-ink-500">Replying reopens this ticket.</span>
                      ) : null}
                    </div>
                  </form>
                </>
              )}
            </Card>
          ) : (
            <>
              <Card>
                <CardTitle>Ask us something</CardTitle>

                {raised ? (
                  <Alert tone={raised.status === "sent" ? "success" : "warn"}>
                    {raised.status === "sent" ? (
                      <>
                        Raised as <strong>{raised.reference}</strong>. We will reply to <strong>{user?.email}</strong>.
                      </>
                    ) : (
                      <>
                        Raised as <strong>{raised.reference}</strong>, but the email was{" "}
                        <strong>not actually sent</strong> — this deployment has no mail server configured, so it was
                        recorded and printed instead. Setting <code>SMTP_URL</code> turns delivery on.
                      </>
                    )}
                  </Alert>
                ) : null}

                <form onSubmit={submit} className="mt-4 space-y-4">
                  {write.errorMessage ? <Alert tone="danger">{write.errorMessage}</Alert> : null}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="What is it about" htmlFor="topic" error={write.fieldErrors.topic}>
                      <Select id="topic" name="topic" defaultValue="question">
                        {TOPICS.map((topic) => (
                          <option key={topic.value} value={topic.value}>
                            {topic.label}
                          </option>
                        ))}
                      </Select>
                    </Field>

                    <Field label="Subject" htmlFor="subject" hint="Optional." error={write.fieldErrors.subject}>
                      <Input id="subject" name="subject" maxLength={140} placeholder="Receipt shows the wrong balance" />
                    </Field>
                  </div>

                  <Field
                    label="How may we help"
                    htmlFor="message"
                    required
                    error={write.fieldErrors.message}
                    hint="What you were doing, what happened, and what you expected instead."
                  >
                    <Textarea id="message" name="message" rows={6} required minLength={10} placeholder="What happened, and what you expected instead." />
                  </Field>

                  <Button type="submit" disabled={write.isPending}>
                    {write.isPending ? "Sending…" : "Send to support"}
                  </Button>
                </form>
              </Card>

              <Card>
                <CardTitle>FAQs</CardTitle>
                {/* Closed by default and opened one at a time: a wall of answers to questions
                    nobody asked is what people scroll past on the way to the form. */}
                <div className="divide-y divide-ink-100">
                  {FAQS.map((faq) => (
                    <details key={faq.question} className="group py-3">
                      <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium text-ink-800">
                        <IconChevronDown
                          size={15}
                          className="shrink-0 text-ink-400 transition-transform group-open:rotate-180"
                        />
                        {faq.question}
                      </summary>
                      <p className="mt-2 pl-[23px] text-sm text-ink-600">{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </Card>
            </>
          )}
        </div>

        <div className="lg:col-span-5">
          <Card padded={false}>
            <div className="p-4 sm:p-5">
              <CardTitle
                action={
                  tickets.data?.page.total ? (
                    <span className="text-sm text-ink-500">{tickets.data.page.total}</span>
                  ) : undefined
                }
              >
                Your tickets
              </CardTitle>

              <div className="-mt-1 mb-3 flex flex-wrap gap-1">
                {FILTERS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={filter === option.value}
                    onClick={() => choose(option.value)}
                    className={`rounded-[var(--radius-control)] px-2.5 py-1 text-xs font-medium transition-colors ${
                      filter === option.value ? "bg-brand-600 text-white" : "bg-ink-100 text-ink-600 hover:bg-ink-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {tickets.isLoading ? (
                <Spinner />
              ) : !tickets.data?.tickets.length ? (
                <EmptyState
                  title={filter === "all" ? "You have not raised anything yet." : `Nothing ${filter}.`}
                  hint={
                    filter === "all"
                      ? "Anything you send from here appears in this list with a reference you can quote."
                      : "Try another filter — the others may have something."
                  }
                />
              ) : (
                <ul className="divide-y divide-ink-100">
                  {tickets.data.tickets.map((ticket) => (
                    <li key={ticket.id}>
                      {/* The whole row opens the thread on the left. A ticket you cannot open is a
                          receipt, not a conversation. */}
                      <button
                        type="button"
                        aria-current={openId === ticket.id ? "true" : undefined}
                        onClick={() => openTicket(ticket.id)}
                        className={`-mx-2 block w-full rounded px-2 py-3 text-left transition-colors ${
                          openId === ticket.id ? "bg-brand-50" : "hover:bg-ink-50"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="mono shrink-0 text-xs font-medium text-ink-500">{ticket.reference}</span>
                          <Badge tone={STATUS_TONE[ticket.status]} dot>
                            {ticket.status}
                          </Badge>
                          {ticket.replies > 0 ? (
                            <span className="text-xs text-ink-500">
                              {ticket.replies} {ticket.replies === 1 ? "reply" : "replies"}
                            </span>
                          ) : null}
                          {ticket.rating ? <Stars value={ticket.rating} small /> : null}
                        </span>

                        <span className="mt-1 block truncate text-sm font-medium text-ink-900">{ticket.subject}</span>
                        <span className="block truncate text-xs text-ink-500">
                          {/* A timestamp, not a calendar day — `fmt.date` expects `YYYY-MM-DD`
                              and rendered this as "NaN Aug 2026". */}
                          {fmt.dateTime(ticket.lastMessageAt)} · {ticket.raisedByName}
                          {ticket.deliveryStatus === "simulated" ? " · not delivered" : ""}
                          {ticket.deliveryStatus === "failed" ? " · delivery failed" : ""}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {tickets.data && tickets.data.page.total > tickets.data.page.perPage ? (
              <Pagination meta={tickets.data.page} params={pager} noun="tickets" />
            ) : null}
          </Card>
        </div>
      </div>
    </div>
  );
}
